import { BillingService } from '@/core/services/billing.service'
import { BillingI } from '../interfaces/billing.interface'
import {
    BillingCalculateCostParams,
    BillingCalculateCostResponseT,
    BillingChangeAccountantPayload,
    BillingDiscountPayloadT,
    BillingGetAvrForPrintResponseT,
    BillingGetCalculationResponseT,
    BillingGetCalculationsParamsT,
    BillingGetCalculationsResponseT,
    BillingGetDiscountsResponseT,
    BillingGetIndividualCompaniesParamsT,
    BillingGetIndividualCompaniesResponseT,
    BillingGetInvoiceCalculationResponseT,
    BillingGetTariffByIdResponseT,
    BillingGetTariffResponseT,
    BillingGetTariffSecondByIdResponseT,
    BillingGetTariffThirdByIdResponseT,
    BillingGetTariffsResponseT,
    BillingGetTranscriptForPrintResponseT,
    BillingGetTranscriptResponseT,
    BillingGetTranscriptsParams,
    BillingGetTranscriptsResponseT,
    BillingGetTtnForPrintResponseT,
    BillingPreviewTranscriptParamsT,
    BillingPreviewTranscriptResponseT,
    CalculationPayloadT,
    CreateTariffPayloadT,
    IndividualTariffAdditionalServicesPayloadT,
    IndividualTariffParamsPayloadT,
    IndividualTariffPayloadT,
    TariffPayloadT,
    UpdateCalculationPayload,
    UpdateTranscriptPayload,
    ManualGenerateTranscriptPayload,
    BillingGetAdditionalServicesPricesParams,
    BillingGetAdditionalServicesPricesResponse,
    BillingUpdateCalculationPricePayload,
    BillingGetCompanyResponseT,
    GetAdditionalServicesTariffsParams,
    InternationalCalculationPayloadT,
    SetCostResponse,
    BillingSetVatPayload,
    GetFtlCalculatorsPayload,
    GetFtlCalculatorsResponse,
    GetFtlTariffsParams,
    GetFtlTariffsResponse,
    GetShipmentCostsFtlParams,
    GetShipmentCostsFtlResponse,
    BillingCalculateFtlCostParams,
    BillingCalculateCostFtlResponseT,
    FtlTariffPayload,
    BillingGetFtlTariffByIdResponse,
    GetStandard2025TariffsResponse,
    GetMinWeightResponse,
} from '../types/billing.types'
import { HistoryInvoiceBillingT } from '@/core/types/invoices.types'

const billingService = new BillingService()

class BillingRealisation implements BillingI {
    getCalculations(
        params: BillingGetCalculationsParamsT
    ): Promise<BillingGetCalculationsResponseT> {
        return billingService.getCalculations(params)
    }

    getMinWeight(id: number): Promise<GetMinWeightResponse> {
        return billingService.getMinWeight(id)
    }

    previewTranscript(
        params: BillingPreviewTranscriptParamsT
    ): Promise<BillingPreviewTranscriptResponseT> {
        return billingService.previewTranscript(params)
    }

    getTariffs(page: number): Promise<BillingGetTariffsResponseT> {
        return billingService.getTariffs(page)
    }

    getTariffsStandard(): Promise<BillingGetTariffsResponseT> {
        return billingService.getTariffsStandard()
    }

    getCompaniesIndividual(
        params: BillingGetIndividualCompaniesParamsT
    ): Promise<BillingGetIndividualCompaniesResponseT> {
        return billingService.getCompaniesIndividual(params)
    }

    getCompaniesFtl(
        params: BillingGetIndividualCompaniesParamsT
    ): Promise<BillingGetIndividualCompaniesResponseT> {
        return billingService.getCompaniesFtl(params)
    }

    getCompaniesUnique(
        params: BillingGetIndividualCompaniesParamsT
    ): Promise<BillingGetIndividualCompaniesResponseT> {
        return billingService.getCompaniesUnique(params)
    }

    getCalculation(calculation_id: number): Promise<BillingGetCalculationResponseT> {
        return billingService.getCalculation(calculation_id)
    }

    getInvoiceCalculation(invoice_id: number): Promise<BillingGetInvoiceCalculationResponseT> {
        return billingService.getInvoiceCalculation(invoice_id)
    }

    getTranscriptForPrint(invoice_number: string): Promise<BillingGetTranscriptForPrintResponseT> {
        return billingService.getTranscriptForPrint(invoice_number)
    }

    getAvrForPrint(invoice_number: string): Promise<BillingGetAvrForPrintResponseT> {
        return billingService.getAvrForPrint(invoice_number)
    }

    getTariff(tariff_id: number): Promise<BillingGetTariffResponseT> {
        return billingService.getTariff(tariff_id)
    }

    getTariffByCompanyId(companyId: number): Promise<BillingGetTariffByIdResponseT> {
        return billingService.getTariffByCompanyId(companyId)
    }

    getFtlTariffsByCompanyId(companyId: number): Promise<BillingGetFtlTariffByIdResponse> {
        return billingService.getFtlTariffsByCompanyId(companyId)
    }

    getTariffSecondByCompanyId(companyId: number): Promise<BillingGetTariffSecondByIdResponseT> {
        return billingService.getTariffSecondByCompanyId(companyId)
    }

    getTariffThirdByCompanyId(
        params: GetAdditionalServicesTariffsParams
    ): Promise<BillingGetTariffThirdByIdResponseT> {
        return billingService.getTariffThirdByCompanyId(params)
    }

    createCalculation(payload: CalculationPayloadT): Promise<void> {
        return billingService.createCalculation(payload)
    }

    setCost(id: number): Promise<SetCostResponse> {
        return billingService.setCost(id)
    }

    createInternationalCalculation(payload: InternationalCalculationPayloadT): Promise<void> {
        return billingService.createInternationalCalculation(payload)
    }

    addIndividualTariff(payload: IndividualTariffPayloadT): Promise<void> {
        return billingService.addIndividualTariff(payload)
    }

    addFtlTariff(payload: FtlTariffPayload): Promise<void> {
        return billingService.addFtlTariff(payload)
    }

    editIndividualTariff(payload: IndividualTariffPayloadT): Promise<void> {
        return billingService.editIndividualTariff(payload)
    }

    addIndividualTariffParams(payload: IndividualTariffParamsPayloadT): Promise<void> {
        return billingService.addIndividualTariffParams(payload)
    }

    editIndividualTariffParams(payload: IndividualTariffParamsPayloadT): Promise<void> {
        return billingService.editIndividualTariffParams(payload)
    }

    addAdditionalServices(payload: IndividualTariffAdditionalServicesPayloadT): Promise<void> {
        return billingService.addAdditionalServices(payload)
    }

    editAdditionalServices(payload: IndividualTariffAdditionalServicesPayloadT): Promise<void> {
        return billingService.editAdditionalServices(payload)
    }

    handleBilling(params: HistoryInvoiceBillingT): Promise<void> {
        return billingService.handleBilling(params)
    }

    getTtnForPrint(invoice_number: string): Promise<BillingGetTtnForPrintResponseT> {
        return billingService.getTtnForPrint(invoice_number)
    }

    updateTariff(tariff_id: number, payload: TariffPayloadT): Promise<void> {
        return billingService.updateTariff(tariff_id, payload)
    }

    getTranscripts(params: BillingGetTranscriptsParams): Promise<BillingGetTranscriptsResponseT> {
        return billingService.getTranscripts(params)
    }

    getTranscript(transcript_id: number): Promise<BillingGetTranscriptResponseT> {
        return billingService.getTranscript(transcript_id)
    }

    downloadFile(file_id: number): Promise<any> {
        return billingService.downloadFile(file_id)
    }

    download1CFile(transcript_id: number): Promise<any> {
        return billingService.download1CFile(transcript_id)
    }

    generateCompanyTranscript(company_id: number): Promise<void> {
        return billingService.generateCompanyTranscript(company_id)
    }

    exportTranscripts(start_date: string, end_date: string): Promise<any> {
        return billingService.exportTranscripts(start_date, end_date)
    }

    createTariff(payload: CreateTariffPayloadT): Promise<void> {
        return billingService.createTariff(payload)
    }

    getDiscounts(page: number): Promise<BillingGetDiscountsResponseT> {
        return billingService.getDiscounts(page)
    }

    createDiscount(payload: BillingDiscountPayloadT): Promise<void> {
        return billingService.createDiscount(payload)
    }

    updateTranscript(transcript_id: number, payload: UpdateTranscriptPayload): Promise<void> {
        return billingService.updateTranscript(transcript_id, payload)
    }

    updateCalculation(calculation_id: number, payload: UpdateCalculationPayload): Promise<void> {
        return billingService.updateCalculation(calculation_id, payload)
    }

    printWaybill(invoice_id: number): Promise<any> {
        return billingService.printWaybill(invoice_id)
    }

    getCompany(company_id: number): Promise<BillingGetCompanyResponseT> {
        return billingService.getCompany(company_id)
    }

    applyAccountantToCompany(company_id: number, accountant_id: number): Promise<void> {
        return billingService.applyAccountantToCompany(company_id, accountant_id)
    }

    changeAccountant(payload: BillingChangeAccountantPayload): Promise<void> {
        return billingService.changeAccountant(payload)
    }

    calculateCost(params: BillingCalculateCostParams): Promise<BillingCalculateCostResponseT> {
        return billingService.calculateCost(params)
    }

    calculateCostV3(params: BillingCalculateCostParams): Promise<BillingCalculateCostResponseT> {
        return billingService.calculateCostV3(params)
    }

    calculateCostFtl(
        params: BillingCalculateFtlCostParams
    ): Promise<BillingCalculateCostFtlResponseT> {
        return billingService.calculateCostFtl(params)
    }

    deleteTranscript(transcript_id: number): Promise<void> {
        return billingService.deleteTranscript(transcript_id)
    }

    manualGenerateTranscript(payload: ManualGenerateTranscriptPayload): Promise<void> {
        return billingService.manualGenerateTranscript(payload)
    }

    getFtlCalculators(payload: GetFtlCalculatorsPayload): Promise<GetFtlCalculatorsResponse> {
        return billingService.getFtlCalculators(payload)
    }

    getShipmentCostsFtl(params: GetShipmentCostsFtlParams): Promise<GetShipmentCostsFtlResponse> {
        return billingService.getShipmentCostsFtl(params)
    }

    getFtlTariffs(params: GetFtlTariffsParams): Promise<GetFtlTariffsResponse> {
        return billingService.getFtlTariffs(params)
    }

    updateFewCalculations(calculationIds: number[]): Promise<void> {
        return billingService.updateFewCalculations(calculationIds)
    }

    getAdditionalServicesPrices(
        params: BillingGetAdditionalServicesPricesParams
    ): Promise<BillingGetAdditionalServicesPricesResponse> {
        return billingService.getAdditionalServicesPrices(params)
    }

    updateCalculationPrice(
        calculation_id: number,
        payload: BillingUpdateCalculationPricePayload
    ): Promise<void> {
        return billingService.updateCalculationPrice(calculation_id, payload)
    }

    setVat(company_id: number, payload: BillingSetVatPayload): Promise<void> {
        return billingService.setVat(company_id, payload)
    }

    importInvoices(form_data: FormData): Promise<void> {
        return billingService.importInvoices(form_data)
    }

    getStandard2025Tariffs(zoneId: number): Promise<GetStandard2025TariffsResponse> {
        return billingService.getStandard2025Tariffs(zoneId)
    }

    createDefaultTariffV3(payload: { weight: number; companyId: number }): Promise<void> {
        return billingService.createDefaultTariffV3(payload)
    }
}

export const billing = new BillingRealisation()
